import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import './CampaignSetup.scss';
import { IconJsxer } from '../../helpers/IconHelper';
import { getStatusForCampaign } from '../../helpers/Streams';
import axios from '../../api/axios';
import { getAllCampaignsV2Route, userListAllUsersRoute, getAllClientsRoute, getAllTemplatesCategoriesRoute, getAirTableDataRoute, campaignSetupRoute } from '../../api/routes';
import { TOP_LEVEL_NAV_ROUTES } from '../../config/NavRoutes';
import { getPrimaryTemplateCategoryId, templateHasCategory } from '../../helpers/templateCategories';
import { AuthContext } from '../../contexts/AuthContext';
import DropDownList from "../../components/forms/DropDownList/DropDownList";
import AdvancedDropDownList from "../../components/forms/DropDownList/AdvancedDropDownList";
import { debounce } from "lodash";
import toDisplayAssetUrl from '../../helpers/DisplayAssetUrl';
import useStationaryTooltip from '../../hooks/useStationaryTooltip';
import TemplateTooltip from '../../components/TemplateTooltip/TemplateTooltip';

const getItemId = (item) => item?.id || item?._id || null;

const TemplateCardItem = ({ template, isSelected, selectionNumber, onToggle }) => {
    const { tooltipVisible, anchorRef, onMouseEnter, onMouseMove, onMouseLeave } = useStationaryTooltip();
    const templateId = getItemId(template);

    return (
        <div
            ref={anchorRef}
            data-template-id={templateId || undefined}
            className={`template-card ${isSelected ? 'selected' : ''}`}
            onClick={() => templateId && onToggle(templateId)}
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <div
                className="template-image"
                style={template.templateImage ? { backgroundImage: `url(${toDisplayAssetUrl(template.templateImage)})` } : {}}
            >
                {!template.templateImage && <div className="placeholder-image">FEATURE IMG</div>}
                {selectionNumber && (
                    <div className="template-number-badge">
                        {selectionNumber}
                    </div>
                )}
            </div>
            <div className="template-info">
                <p className="template-type">{template.featureType}</p>
                <h3 className="template-name">{template.name}</h3>
            </div>
            <TemplateTooltip
                anchorRef={anchorRef}
                visible={tooltipVisible}
                title={template.name}
                description={template.description}
            />
        </div>
    );
};

// Campaign filter constants
const TAB_MY = "my";
const TAB_STARRED = "starred"; 
const TAB_ARCHIVED = "archived";
const TAB_ALL = "all";

const CAMPAIGN_SORT_OPTIONS = [
    { label: "Last modified", value: "Last modified" },
    { label: "Last modified by me", value: "Last modified by me" },
    { label: "Last modified by others", value: "Last modified by others" },
    { label: "Starred", value: "Starred" },
    { label: "Upcoming streams", value: "Upcoming streams" },
    { label: "Live streams", value: "Live streams" },
    { label: "Campaign name A to Z", value: "Campaign name A to Z" },
    { label: "Owner A to Z", value: "Owner A to Z" },
    { label: "Client A to Z", value: "Client A to Z" },
];

const CAMPAIGN_STATUS_OPTIONS = [
    { label: "All", value: "", deselect: true },
    { label: "Active", value: "active" },
    { label: "Draft", value: "draft" },
];

const MODIFIED_OPTIONS = [
    { label: "All", value: "", deselect: true },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This week", value: "this-week" },
    { label: "Last week", value: "last-week" },
    { label: "This month", value: "this-month" },
    { label: "Last month", value: "last-month" },
];

const CampaignSetup = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedOption, setSelectedOption] = useState(null);
    const [currentView, setCurrentView] = useState('starter'); // 'starter', 'categories', 'templates', 'campaign-details', 'salesforce-number', 'details-recap'
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [templateType, setTemplateType] = useState('feature'); // 'feature' or 'campaign'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFeatureFilters, setSelectedFeatureFilters] = useState([]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [selectedCampaignToDuplicate, setSelectedCampaignToDuplicate] = useState(null);
    const [campaignSearchTerm, setCampaignSearchTerm] = useState('');
    const [activeCampaignTab, setActiveCampaignTab] = useState(TAB_MY);
    const [showAllTemplates, setShowAllTemplates] = useState(false);

    // Ref used to scroll to a pre-selected template after arriving from Inspiration / Home
    const pendingScrollTemplateId = useRef(null);
    const templateRequestInFlight = useRef(false);
    
    // Real campaign data from API
    const [allCampaigns, setAllCampaigns] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [peopleFilter, setPeopleFilter] = useState([]);
    const [clientFilter, setClientFilter] = useState([]);
    const [modifiedOption, setModifiedOption] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortOption, setSortOption] = useState('Last modified');

    // Real template data from API
    const [templateCategories, setTemplateCategories] = useState([]);
    const [allTemplates, setAllTemplates] = useState([]);
    const [featureTypes, setFeatureTypes] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    // Unique regions for dropdown (extracted from all clients)
    const [uniqueRegions, setUniqueRegions] = useState([]);

    // Campaign data for step 3
    const [campaignData, setCampaignData] = useState({
        campaignName: '',
        platform: 'Twitch', // Default to Twitch
        features: [],
        client: '',
        region: '',
        // Will be populated based on selected option (duplicate/template/blank)
    });

    // Salesforce number data for step 4 (only for Twitch)
    const [salesforceData, setSalesforceData] = useState({
        sfNumber: '',
        isVerified: false,
        isLoading: false,
        error: '',
        airtableData: null
    });

    // Track the highest step the user has actually reached
    const [maxReachedStep, setMaxReachedStep] = useState(1);

    // Track the furthest subsection reached within step 3
    const [maxReachedSubsection, setMaxReachedSubsection] = useState('name'); // 'name', 'salesforce-number', 'details-recap'

    // State preservation for navigation
    const [savedStepData, setSavedStepData] = useState({
        step1: { selectedOption: null },
        step2: { 
            selectedCategory: null, 
            templateType: 'feature', 
            selectedTemplates: [], 
            selectedFeatureFilters: [],
            searchTerm: '',
            currentView: 'categories'
        }
    });

    // API calls and data loading
    const [authContext, setAuthContext] = useContext(AuthContext);

    // Compute available feature types based on current templateType
    const availableFeatureTypes = useMemo(() => {
        // Get all templates for the current templateType
        const templatesForType = allTemplates.filter(template => 
            template.templateType === templateType
        );
        
        // Extract unique feature types from these templates
        const uniqueTypes = [...new Set(templatesForType.map(template => template.featureType))];
        
        // Filter the original featureTypes array to only include types that have templates
        return featureTypes.filter(type => uniqueTypes.includes(type));
    }, [allTemplates, templateType, featureTypes]);

    // After arriving from Inspiration / Home with a pre-selected template, scroll to it
    useEffect(() => {
        if (currentView === 'templates' && pendingScrollTemplateId.current) {
            const id = pendingScrollTemplateId.current;
            // Small timeout lets the DOM render first
            const timer = setTimeout(() => {
                const el = document.querySelector(`[data-template-id="${id}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    pendingScrollTemplateId.current = null;
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [currentView]);

    const loadTemplateData = useCallback(async () => {
        if (templateRequestInFlight.current) {
            return;
        }

        templateRequestInFlight.current = true;
        setTemplatesLoading(true);
        try {
            console.log('Making API call to:', getAllTemplatesCategoriesRoute);
            const categoriesResponse = await axios.get(getAllTemplatesCategoriesRoute, { withCredentials: true });
            console.log('Full API Response:', categoriesResponse);
            console.log('Response status:', categoriesResponse.status);
            console.log('Response headers:', categoriesResponse.headers);
            console.log('Response data:', categoriesResponse.data);
            console.log('Response data type:', typeof categoriesResponse.data);

            const responseData = categoriesResponse?.data?.data || {};
            console.log('Extracted responseData:', responseData);
            const categories = responseData.categories || [];
            const templates = responseData.templates || [];
            const nextFeatureTypes = responseData.featureTypes || [];

            console.log('Categories:', categories);
            console.log('Templates:', templates);
            console.log('Feature Types:', nextFeatureTypes);

            setTemplateCategories(categories);
            setAllTemplates(templates);
            setFeatureTypes(nextFeatureTypes);

            const fromInspiration = location.state?.fromInspiration;
            const inspirationTemplateId = location.state?.templateId;
            const inspirationTemplateType = location.state?.templateType;
            if (fromInspiration && inspirationTemplateId) {
                const matched = templates.find(t => getItemId(t) === inspirationTemplateId);
                if (matched) {
                    setSelectedOption('templates');
                    setTemplateType(inspirationTemplateType || matched.templateType || 'feature');
                    setSelectedCategory(getPrimaryTemplateCategoryId(matched));
                    setSelectedTemplates([getItemId(matched) || inspirationTemplateId]);
                    setCurrentStep(2);
                    setCurrentView('templates');
                    setMaxReachedStep(2);
                    pendingScrollTemplateId.current = getItemId(matched) || inspirationTemplateId;
                    navigate(location.pathname, { replace: true, state: null });
                }
            }
        } catch (error) {
            console.error('Error loading template data:', error);
        } finally {
            templateRequestInFlight.current = false;
            setTemplatesLoading(false);
        }
    }, [location.pathname, location.state, navigate]);


    // Load users and clients data
    useEffect(() => {
        axios.get(userListAllUsersRoute, { withCredentials: true })
            .then(response => {
                console.log('Users API response:', response.data);
                const users = response.data.allusers || response.data || [];
                setAllUsers(users.map(user => ({
                    _id: user._id,
                    name: user.name,
                    displayName: user.name
                })));
            })
            .catch(error => console.error('Error loading users:', error));

        axios.get(getAllClientsRoute, { withCredentials: true })
            .then(response => {
                console.log('Clients API response:', response.data);
                const clients = response.data.clients || response.data || [];
                const clientsData = clients.map(client => ({
                    clientName: client.clientName,
                    regions: client.regions || []
                }));
                setAllClients(clientsData);
                
                // Extract unique regions from all clients
                const regionsSet = new Set();
                clientsData.forEach(client => {
                    if (client.regions && Array.isArray(client.regions)) {
                        client.regions.forEach(region => {
                            if (region && typeof region === 'string') {
                                regionsSet.add(region);
                            }
                        });
                    }
                });
                setUniqueRegions(Array.from(regionsSet));
            })
            .catch(error => console.error('Error loading clients:', error));
    }, []);

    // Load template data on component mount
    useEffect(() => {
        loadTemplateData();
    }, [loadTemplateData]);

    useEffect(() => {
        const isTemplateFlow = selectedOption === 'templates' && (currentView === 'categories' || currentView === 'templates');
        const hasTemplateData = templateCategories.length > 0 || allTemplates.length > 0 || featureTypes.length > 0;

        if (isTemplateFlow && !hasTemplateData && !templatesLoading) {
            loadTemplateData();
        }
    }, [allTemplates.length, currentView, featureTypes.length, loadTemplateData, selectedOption, templateCategories.length, templatesLoading]);

    // Load campaigns data - exactly like Campaigns.jsx
    useEffect(() => {
        console.log('Campaign loading useEffect triggered - currentStep:', currentStep);
        
        let peopleArr = [];
        let status = statusFilter;
        let filters = {
            people: [],
            status: "",
            client: clientFilter,
            modified: modifiedOption,
            starredCampaigns: null,
            archived: null,
            searchTerm: campaignSearchTerm || "",
            page: 1,
            pageSize: 50
        };

        if (activeCampaignTab === TAB_MY) {
            // Only show campaigns where user is owner or producer
            if (authContext && authContext.userData) {
                peopleArr = {
                    "filter1": [
                        { userId: authContext.userData._id, type: "owner" },
                        { userId: authContext.userData._id, type: "shared" },
                    ],
                    "filter2": [
                        ...peopleFilter.filter((val) => val.type !== "owner").map(p => ({
                            userId: p._id,
                            type: "shared"
                        }))
                    ]
                };
            }
            filters.archived = false;
            filters.people = peopleArr;
            filters.status = status; // allow status filter (no archived)
        } else if (activeCampaignTab === TAB_STARRED) {
            // Only show campaigns starred by the user
            console.log('TAB_STARRED - authContext:', authContext);
            console.log('TAB_STARRED - userData:', authContext?.userData);
            console.log('TAB_STARRED - starredCampaigns:', authContext?.userData?.starredCampaigns);
            if (authContext && authContext.userData) {
                peopleArr = {
                    "filter1": [
                        { userId: authContext.userData._id, type: "owner" },
                        { userId: authContext.userData._id, type: "shared" },
                    ],
                    "filter2": [
                        ...peopleFilter.filter((val) => val.type !== "owner").map(p => ({
                            userId: p._id,
                            type: "shared"
                        }))
                    ]
                };
            }
            filters.archived = false;
            filters.people = peopleArr;
            filters.starredCampaigns = authContext?.userData?.starredCampaigns || [];
            console.log('TAB_STARRED - final filters.starredCampaigns:', filters.starredCampaigns);
        } else if (activeCampaignTab === TAB_ARCHIVED) {
            // Only show the user's archived campaigns
            if (authContext && authContext.userData) {
                peopleArr = {
                    "filter1": [
                        { userId: authContext.userData._id, type: "owner" },
                        { userId: authContext.userData._id, type: "shared" },
                    ],
                    "filter2": [
                        ...peopleFilter.filter((val) => val.type !== "owner").map(p => ({
                            userId: p._id,
                            type: "shared"
                        }))
                    ]
                };
            }
            filters.archived = true;
            filters.people = peopleArr;
            filters.status = "archived";
        } else if (activeCampaignTab === TAB_ALL) {
            // All campaigns, allow status filter (no archived in dropdown)
            filters.people = {
                "filter2": peopleFilter.map(p => ({
                    userId: p._id,
                    type: p.type
                }))
            };
            filters.status = status;
        }

        console.log("filters.people = ", filters.people);
        console.log('Loading campaigns with filters:', filters);
        console.log('AuthContext:', authContext);
        
        axios.post(getAllCampaignsV2Route, filters, { withCredentials: true })
            .then(response => {
                console.log('Campaign data received:', response.data);
                console.log('Campaigns array:', response.data.campaigns);
                console.log('User starred campaigns:', authContext?.userData?.starredCampaigns);
                console.log('Current user ID:', authContext?.userData?._id);
                console.log('AuthContext user ID:', authContext?.userData?._id);
                console.log('Current user:', authContext?.userData);
                setAllCampaigns(response.data.campaigns || []);
            })
            .catch(error => {
                console.error('Error loading campaigns:', error);
                console.error('Error details:', error.response?.data);
                setAllCampaigns([]);
            });
    }, [currentStep, activeCampaignTab, campaignSearchTerm, peopleFilter, clientFilter, modifiedOption, statusFilter, sortOption, authContext]);

    // Debounced search handler
    const debouncedSearchChange = debounce((value) => {
        setCampaignSearchTerm(value);
    }, 300);

    const handleSearchChange = (e) => {
        debouncedSearchChange(e.target.value);
    };

    // Helper functions for filters
    const getIndexByValue = (options, value) => {
        const index = options.findIndex(opt => opt.value === value);
        return index >= 0 ? index : null;
    };

    const handleModifiedOption = (e, id, item) => {
        setModifiedOption(item.deselect ? '' : item.value);
    };

    const handleSortOption = (e, id, item) => {
        setSortOption(item.value);
    };

    const handleStatusOption = (e, id, item) => {
        setStatusFilter(item.deselect ? '' : item.value);
    };

    // Generate summaries for dropdowns
    const peopleSummary = useMemo(() => {
        if (peopleFilter.length === 0) return "Add People";
        if (peopleFilter.length === 1) return peopleFilter[0].name;
        return `${peopleFilter.length} people selected`;
    }, [peopleFilter]);

    const clientSummary = useMemo(() => {
        if (clientFilter.length === 0) return "Add Client";
        if (clientFilter.length === 1) return clientFilter[0].clientName;
        return `${clientFilter.length} clients selected`;
    }, [clientFilter]);

    // AirTable integration for Salesforce number verification
    const fetchAirTableDataWithNumber = (sfNum) => {
        if (!sfNum) {
            setSalesforceData(prev => ({ ...prev, error: 'Please enter a Salesforce number first' }));
            return;
        }
        
        setSalesforceData(prev => ({ ...prev, error: '', isLoading: true }));
        
        axios.get(getAirTableDataRoute, { 
            params: { ioNumber: sfNum }, 
            withCredentials: true 
        })
        .then((response) => {
            if (response.data.success && response.data.data.records && response.data.data.records.length > 0) {
                const record = response.data.data.records[0];
                const fields = record.fields;
                
                setSalesforceData(prev => ({ 
                    ...prev, 
                    isVerified: true, 
                    airtableData: fields,
                    error: '' 
                }));
                
                // Auto-populate campaign data with AirTable data
                setCampaignData(prev => {
                    const newData = { ...prev };
                    
                    if (fields['ClientAccountName']) {
                        newData.client = newData.clientName 
                            = ((fields.clientAccountNames && fields.clientAccountNames.length > 0) ? fields.clientAccountNames.join(", ") : fields.clientAccountNames) || null;
                    } else if (fields['Brand']) {
                        newData.client = fields['Brand'];
                        newData.clientName = fields['Brand'];
                    }
                    
                    if (fields['Influencer Broadcast Country']) {
                        newData.region = fields['Influencer Broadcast Country'][0];
                    }

                    if (fields['Streamer Tier']) {
                        if (fields['Streamer Tier'].indexOf("T3") > -1) {
                            newData.campaignLevel = 'basic';
                        } else {
                            newData.campaignLevel = 'custom';
                        }
                    }
                    
                    return newData;
                });
            } else {
                setSalesforceData(prev => ({ 
                    ...prev, 
                    error: 'No data found for this IO Number in AirTable',
                    isVerified: false 
                }));
            }
        })
        .catch((error) => {
            console.error("Error fetching AirTable data:", error);
            setSalesforceData(prev => ({ 
                ...prev, 
                error: 'Failed to fetch data from AirTable',
                isVerified: false 
            }));
        })
        .finally(() => {
            setSalesforceData(prev => ({ ...prev, isLoading: false }));
        });
    };

    // Display campaigns data directly from API (no client-side filtering like Campaigns.jsx)
    const campaignsData = useMemo(() => {
        const mapped = allCampaigns.map(campaign => {
            const status = getStatusForCampaign(campaign.streams, campaign.archived);
            return {
                ...campaign,
                status: status.mapToForCount,
                statusData: status
            };
        });

        const sorted = [...mapped];
        switch (sortOption) {
            case 'Campaign name A to Z':
                sorted.sort((a, b) => (a.campaignName || '').localeCompare(b.campaignName || ''));
                break;
            case 'Owner A to Z':
                sorted.sort((a, b) => (a.ownerName || '').localeCompare(b.ownerName || ''));
                break;
            case 'Client A to Z':
                sorted.sort((a, b) => (a.client || '').localeCompare(b.client || ''));
                break;
            case 'Last modified by me': {
                const myId = authContext?.userData?._id;
                sorted.sort((a, b) => {
                    const aIsMe = a.lastModifiedBy === myId ? 1 : 0;
                    const bIsMe = b.lastModifiedBy === myId ? 1 : 0;
                    if (bIsMe !== aIsMe) return bIsMe - aIsMe;
                    return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                });
                break;
            }
            case 'Last modified by others': {
                const myId = authContext?.userData?._id;
                sorted.sort((a, b) => {
                    const aIsOther = a.lastModifiedBy !== myId ? 1 : 0;
                    const bIsOther = b.lastModifiedBy !== myId ? 1 : 0;
                    if (bIsOther !== aIsOther) return bIsOther - aIsOther;
                    return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                });
                break;
            }
            case 'Starred': {
                const starred = authContext?.userData?.starredCampaigns || [];
                sorted.sort((a, b) => {
                    const aStarred = starred.includes(a._id) ? 1 : 0;
                    const bStarred = starred.includes(b._id) ? 1 : 0;
                    return bStarred - aStarred;
                });
                break;
            }
            case 'Live streams':
                sorted.sort((a, b) => (b.status === 'live' ? 1 : 0) - (a.status === 'live' ? 1 : 0));
                break;
            case 'Upcoming streams':
                sorted.sort((a, b) => (b.status === 'scheduled' ? 1 : 0) - (a.status === 'scheduled' ? 1 : 0));
                break;
            case 'Last modified':
            default:
                sorted.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
                break;
        }
        return sorted;
    }, [allCampaigns, sortOption, authContext]);

    // Calculate dynamic line height for connecting step 2 to "Select templates" or "Select campaign"
    useEffect(() => {
        const calculateLineHeight = () => {
            if (currentStep === 2) {
                setTimeout(() => {
                    const step2Element = document.querySelector('[data-step="2"] .step-indicator');
                    
                    // Handle templates connection
                    if (currentView === 'categories' || currentView === 'templates') {
                        const selectTemplatesElement = document.querySelector('.step-selection[data-select-templates="true"]');
                        
                        if (step2Element && selectTemplatesElement) {
                            const step2Rect = step2Element.getBoundingClientRect();
                            const selectRect = selectTemplatesElement.getBoundingClientRect();
                            
                            // Calculate the distance from step 2 bottom to the top of the bullet
                            const distance = selectRect.top - step2Rect.bottom + 7;
                            
                            // Apply the dynamic height directly to the element
                            selectTemplatesElement.style.setProperty('--connect-height', `${Math.abs(distance)}px`);
                        }
                    }
                    
                    // Handle campaign connection
                    if (currentView === 'campaign-duplicates') {
                        // First try to find the selected campaign name, then fallback to "Select campaign"
                        let selectCampaignElement = null;
                        
                        if (selectedCampaignToDuplicate) {
                            // Look for the selected campaign name bullet
                            const campaign = allCampaigns.find(c => c._id === selectedCampaignToDuplicate);
                            if (campaign) {
                                // Find the element containing the campaign name
                                const stepElements = document.querySelectorAll('.step-selection');
                                selectCampaignElement = Array.from(stepElements).find(el => 
                                    el.textContent.trim() === campaign.campaignName
                                );
                            }
                        } else {
                            // Look for the "Select campaign" prompt
                            selectCampaignElement = document.querySelector('.step-selection[data-select-campaign="true"]');
                        }
                        
                        if (step2Element && selectCampaignElement) {
                            const step2Rect = step2Element.getBoundingClientRect();
                            const selectRect = selectCampaignElement.getBoundingClientRect();
                            
                            // Calculate the distance from step 2 bottom to the top of the bullet
                            const distance = selectRect.top - step2Rect.bottom + 10;
                            
                            // Apply the dynamic height directly to the element
                            selectCampaignElement.style.setProperty('--connect-height', `${Math.abs(distance)}px`);
                        }
                    }
                }, 100); // Increase timeout to ensure DOM is ready
            }
        };

        calculateLineHeight();
    }, [currentStep, currentView, selectedTemplates, selectedCampaignToDuplicate, allCampaigns]);

    // Reset showAllTemplates when search term changes
    useEffect(() => {
        setShowAllTemplates(false);
    }, [searchTerm]);

    useEffect(() => {
        if (currentStep > 1 && !selectedOption) {
            setCurrentStep(1);
            setCurrentView('starter');
            return;
        }

        if (selectedOption === 'templates' && currentStep === 2 && currentView === 'templates' && !selectedCategory) {
            setCurrentView('categories');
        }

        if (selectedOption === 'duplicate' && currentStep === 2 && currentView !== 'campaign-duplicates') {
            setCurrentView('campaign-duplicates');
        }
    }, [currentStep, currentView, selectedCategory, selectedOption]);

    useEffect(() => {
        const isTemplateFlow = selectedOption === 'templates' && currentStep === 2;
        if (!isTemplateFlow || selectedCategory || templatesLoading) {
            return;
        }

        const matchingCategories = getFilteredCategories();
        if (matchingCategories.length === 1) {
            const onlyCategoryId = getItemId(matchingCategories[0]);
            if (onlyCategoryId) {
                setSelectedCategory(onlyCategoryId);
                setCurrentView('templates');
            }
        }
    }, [allTemplates, currentStep, currentView, featureTypes, selectedCategory, selectedFeatureFilters, selectedOption, searchTerm, templateCategories, templateType, templatesLoading]);

    // Steps array - dynamic based on selected option
    const getSteps = () => {
        if (selectedOption === 'blank') {
            // For blank canvas: only 2 steps
            return [
                { id: 1, name: 'Starter option', status: 'current' },
                { id: 2, name: 'Campaign details', status: 'upcoming' }
            ];
        } else {
            // For templates/duplicate: 3 steps
            return [
                { id: 1, name: 'Starter option', status: 'current' },
                { id: 2, name: getStep2Name(), status: 'upcoming' },
                { id: 3, name: 'Campaign details', status: 'upcoming' }
            ];
        }
    };

    const getStep2Name = () => {
        if (selectedOption === 'templates') return 'Templates';
        if (selectedOption === 'duplicate') return 'Campaign duplicate';
        return 'Select starter option'; // fallback
    };

    const steps = getSteps();

    // Mock data for development - now replaced with real API data
    // Categories and templates will be loaded from API

    // Feature types will be loaded from API
    // Templates will be loaded from API

    const starterOptions = [
        {
            id: 'templates',
            title: 'Templates',
            description: 'Jumpstart your campaign with feature and campaign templates.',
            subText: 'All customisation options remain available so you can freely customise the starting features—or even remove it if you change your mind, and add new ones.',
            icon: '/assets/icons/Templates.svg'
        },
        {
            id: 'duplicate',
            title: 'Campaign duplicate',
            description: 'Copy one of your existing campaigns to start with its structure already in place.',
            subText: 'All customisation options remain available so you can freely customise duplicated features and add new ones.',
            icon: '/assets/icons/Duplicate_Files.svg'
        },
        {
            id: 'blank',
            title: 'Blank canvas',
            description: 'Start with a blank canvas.',
            subText: 'Add your features later directly from your workspace.',
            icon: '/assets/icons/Blank_Canvas.svg'
        }
    ];

    const handleOptionSelect = (optionId) => {
        // If changing to a different option, reset progress and clear related data
        if (selectedOption && selectedOption !== optionId) {
            // Reset maxReachedStep to 1 when changing starter option
            // User must complete the new flow to access later steps
            setMaxReachedStep(1);
            
            if (selectedOption === 'templates' && optionId !== 'templates') {
                // Changing away from templates - clear template selections
                setSelectedCategory(null);
                setSelectedTemplates([]);
                setSelectedFeatureFilters([]);
                setSearchTerm('');
                setTemplateType('feature');
            }
            if (selectedOption === 'duplicate' && optionId !== 'duplicate') {
                // Changing away from duplicate - clear campaign selection
                setSelectedCampaignToDuplicate(null);
            }
            
            // Clear any campaign data from previous selection
            setCampaignData({
                campaignName: '',
                platform: 'Twitch',
                features: [],
            });
            
            // Clear saved step data when changing starter option
            setSavedStepData({
                step1: { selectedOption: null },
                step2: { 
                    selectedCategory: null, 
                    templateType: 'feature', 
                    selectedTemplates: [], 
                    selectedFeatureFilters: [],
                    searchTerm: '',
                    currentView: 'categories'
                }
            });
        }
        
        // Always update the selected option (even if it's the same one)
        // This handles the case where user navigates back and re-selects the same option
        setSelectedOption(optionId);
        // Don't automatically navigate - wait for confirm button
    };

    const handleConfirmOption = () => {
        if (selectedOption === 'templates') {
            setCurrentView('categories');
            setCurrentStep(2);
            setMaxReachedStep(Math.max(maxReachedStep, 2)); // Update max reached step
        } else if (selectedOption === 'duplicate') {
            setCurrentView('campaign-duplicates');
            setCurrentStep(2);
            setMaxReachedStep(Math.max(maxReachedStep, 2)); // Update max reached step
        } else if (selectedOption === 'blank') {
            // For blank canvas, skip to campaign details (which is step 2 in this flow)
            // No need to prepare campaign data here - it will be handled in handleCreateCampaign
            setCurrentStep(2); // Step 2 for blank canvas is campaign details
            setCurrentView('campaign-details');
            setMaxReachedStep(Math.max(maxReachedStep, 2)); // Update max reached step
        } else if (selectedOption) {
            // Fallback for other options
            // No need to prepare campaign data here - it will be handled in handleCreateCampaign
            setCurrentStep(3);
            setCurrentView('campaign-details');
            setMaxReachedStep(Math.max(maxReachedStep, 3)); // Update max reached step
            console.log('Selected option:', selectedOption);
        }
    };

    const handleCategorySelect = (categoryId) => {
        setSelectedCategory(categoryId);
        setCurrentView('templates');
    };

    const handleTemplateToggle = (templateId) => {
        setSelectedTemplates(prev => {
            if (prev.includes(templateId)) {
                // Remove the template and maintain order for remaining templates
                return prev.filter(id => id !== templateId);
            } else {
                // Add the template to the end of the array to maintain selection order
                return [...prev, templateId];
            }
        });
    };

    const getTemplateSelectionNumber = (templateId) => {
        const index = selectedTemplates.indexOf(templateId);
        return index !== -1 ? index + 1 : null;
    };

    const handleTemplateTypeChange = (type) => {
        setTemplateType(type);
        setShowAllTemplates(false);
    };

    const handleFeatureFilterToggle = (filter) => {
        setSelectedFeatureFilters(prev => {
            if (prev.includes(filter)) {
                return prev.filter(f => f !== filter);
            } else {
                return [...prev, filter];
            }
        });
        setShowAllTemplates(false);
    };

    const handleBackToCategories = () => {
        setCurrentView('categories');
        setSelectedCategory(null);
        // Don't clear selectedTemplates - preserve them when going back to categories
    };

    const handleConfirmTemplates = () => {
        if (selectedTemplates.length > 0) {
            // Save current step 2 data before navigating
            saveCurrentStepData();
            // No need to prepare campaign data here - it will be handled in handleCreateCampaign
            setCurrentStep(3);
            setCurrentView('campaign-details');
            setMaxReachedStep(Math.max(maxReachedStep, 3)); // Update max reached step
            console.log('Selected templates:', selectedTemplates);
        }
    };

    const handleCampaignSelect = (campaignId) => {
        setSelectedCampaignToDuplicate(campaignId);
    };

    const handleConfirmCampaignDuplicate = () => {
        if (selectedCampaignToDuplicate) {
            // Save current step 2 data before navigating
            saveCurrentStepData();
            
            // Set the campaign name to "Copy of..." for the duplicate
            const campaignToDuplicate = allCampaigns.find(c => c._id === selectedCampaignToDuplicate);
            if (campaignToDuplicate) {
                setCampaignData(prev => ({
                    ...prev,
                    campaignName: `Copy of ${campaignToDuplicate.campaignName}`,
                    platform: campaignToDuplicate.platform || 'Twitch'
                }));
            }
            
            setCurrentStep(3);
            setCurrentView('campaign-details');
            setMaxReachedStep(Math.max(maxReachedStep, 3)); // Update max reached step
            console.log('Selected campaign to duplicate:', selectedCampaignToDuplicate);
        }
    };

    const handleCreateCampaign = async () => {
        try {
            // Determine source type and prepare data
            let sourceType, sourceData;
            
            if (selectedOption === 'blank') {
                sourceType = 'blank';
                sourceData = null;
            } else if (selectedOption === 'duplicate') {
                sourceType = 'duplicate';
                sourceData = {
                    campaignId: selectedCampaignToDuplicate
                };
            } else if (selectedOption === 'templates') {
                sourceType = 'templates';
                sourceData = {
                    templateIds: selectedTemplates
                };
            } else {
                console.error('Invalid selected option:', selectedOption);
                return;
            }
            let clientName = (Array.isArray(campaignData.client) ? campaignData.client.join(', ') : campaignData.client) || '';
            // Prepare campaign details from current campaign data
            const campaignDetails = {
                campaignName: campaignData.campaignName,
                campaignTier: campaignData.campaignTier,
                draft: campaignData.draft !== false, // Default to true if not specified
                region: campaignData.region,
                client: clientName,
                adBugCopy: campaignData.adBugCopy || clientName,
                campaignOwnerAccount: campaignData.campaignOwnerAccount || authContext?.userData?._id,
                campaignOwnerName: campaignData.campaignOwnerName || authContext?.userData?.name,
                producerAccounts: campaignData.producerAccounts || [],
                clientAccounts: campaignData.clientAccounts || [],
                sfNumber: salesforceData.sfNumber,
                sfLink: salesforceData.sfLink,
                features: campaignData.features || [],
                chains: campaignData.chains || [],
                streams: campaignData.streams || []
            };
            
            console.log('Creating campaign with:', { sourceType, sourceData, campaignDetails });
            
            // Make API call to new setup route
            const response = await axios.post(campaignSetupRoute, {
                sourceType,
                sourceData,
                campaignDetails
            }, { withCredentials: true });
            
            if (response.data.success) {
                console.log('Campaign created successfully:', response.data.campaign);
                // Navigate to campaigns list
                navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGNS);
            } else {
                console.error('Campaign creation failed:', response.data.error);
            }
        } catch (error) {
            console.error('Error creating campaign:', error);
            const msg = error?.response?.data?.error || error?.message || 'Failed to create campaign';
            toast.error(msg);
        }
    };

    const getStepDetails = (step) => {
        switch (step.id) {
            case 1:
                if (selectedOption) {
                    const option = starterOptions.find(opt => opt.id === selectedOption);
                    return option ? [{ type: 'prompt', text: option.title }] : [];
                }
                return [];
            case 2:
                const details = [];
                
                // For blank canvas, step 2 is campaign details
                if (selectedOption === 'blank') {
                    // Only show step 2 details if we've actually reached step 2
                    if (maxReachedStep >= 2) {
                        // Show "Name" with bullet point and connecting line
                        details.push({ type: 'selection', text: 'Name' });
                        
                        // Show campaign name value if entered, otherwise show prompt (no bullet point)
                        if (campaignData.campaignName.trim()) {
                            details.push({ type: 'value', text: campaignData.campaignName });
                        } else {
                            details.push({ type: 'value', text: 'Enter name' });
                        }

                        // Add Salesforce number section only for Twitch platform
                        if (campaignData.platform === 'Twitch') {
                            // Show "Salesforce number" with bullet point and connecting line
                            details.push({ type: 'selection', text: 'Salesforce number' });
                            
                            // Show SF number value if entered, otherwise show prompt (no bullet point)
                            if (salesforceData.sfNumber.trim()) {
                                details.push({ type: 'value', text: salesforceData.sfNumber });
                            } else {
                                details.push({ type: 'value', text: 'Enter number' });
                            }
                        }

                        // Add Details recap section if it has been reached
                        const subsectionOrder = ['name', 'salesforce-number', 'details-recap'];
                        const maxReachedIndex = subsectionOrder.indexOf(maxReachedSubsection);
                        const detailsRecapIndex = subsectionOrder.indexOf('details-recap');
                        if (maxReachedIndex >= detailsRecapIndex) {
                            details.push({ type: 'selection', text: 'Details recap' });
                        }
                    }
                    return details;
                }
                
                // For templates/duplicate flows, step 2 is templates/duplicate selection
                // Only show step 2 details if we've actually reached step 2
                if (maxReachedStep >= 2) {
                    // Handle templates flow
                    if (selectedOption === 'templates') {
                        // Add selected templates
                        if (selectedTemplates.length > 0) {
                            selectedTemplates.forEach(templateId => {
                                const template = allTemplates.find(t => getItemId(t) === templateId);
                                if (template) {
                                    details.push({ type: 'selection', text: template.name });
                                }
                            });
                        }
                        
                        // Add "Select templates" prompt
                        if (currentView === 'categories' || currentView === 'templates') {
                            const promptText = selectedTemplates.length > 0 
                                ? 'Select templates (optional)' 
                                : 'Select templates';
                            details.push({ type: 'selection', text: promptText });
                        }
                    }
                    
                    // Handle campaign duplicate flow
                    if (selectedOption === 'duplicate') {
                        if (selectedCampaignToDuplicate) {
                            const campaign = allCampaigns.find(c => c._id === selectedCampaignToDuplicate);
                            if (campaign) {
                                details.push({ type: 'selection', text: campaign.campaignName });
                            }
                        }
                        
                        // Add "Select campaign" prompt only when no campaign is selected
                        if (currentView === 'campaign-duplicates' && !selectedCampaignToDuplicate) {
                            details.push({ type: 'selection', text: 'Select campaign' });
                        }
                    }
                }
                
                return details;
            case 3:
                // Step 3 only exists for templates/duplicate flows (not blank canvas)
                if (selectedOption === 'blank') {
                    return []; // No step 3 for blank canvas
                }
                
                const step3Details = [];
                
                // Only show step 3 details if we've actually reached step 3
                if (maxReachedStep >= 3) {
                    // Show "Name" with bullet point and connecting line
                    step3Details.push({ type: 'selection', text: 'Name' });
                    
                    // Show campaign name value if entered, otherwise show prompt (no bullet point)
                    if (campaignData.campaignName.trim()) {
                        step3Details.push({ type: 'value', text: campaignData.campaignName });
                    } else {
                        step3Details.push({ type: 'value', text: 'Enter name' });
                    }

                    // Add Salesforce number section only for Twitch platform
                    if (campaignData.platform === 'Twitch') {
                        // Show "Salesforce number" with bullet point and connecting line
                        step3Details.push({ type: 'selection', text: 'Salesforce number' });
                        
                        // Show SF number value if entered, otherwise show prompt (no bullet point)
                        if (salesforceData.sfNumber.trim()) {
                            step3Details.push({ type: 'value', text: salesforceData.sfNumber });
                        } else {
                            step3Details.push({ type: 'value', text: 'Enter number' });
                        }
                    }

                    // Add Details recap section if it has been reached
                    const subsectionOrder = ['name', 'salesforce-number', 'details-recap'];
                    const maxReachedIndex = subsectionOrder.indexOf(maxReachedSubsection);
                    const detailsRecapIndex = subsectionOrder.indexOf('details-recap');
                    if (maxReachedIndex >= detailsRecapIndex) {
                        step3Details.push({ type: 'selection', text: 'Details recap' });
                    }
                }
                
                return step3Details;
            default:
                return [];
        }
    };

    const getStepDisplayName = (step) => {
        if (step.id === 2) {
            if (selectedOption === 'templates' && (currentView === 'categories' || currentView === 'templates')) {
                return 'Templates';
            }
            if (selectedOption === 'duplicate' && currentView === 'campaign-duplicates') {
                return 'Campaign duplicate';
            }
        }
        return step.name;
    };

    // Handle step navigation
    const handleStepClick = (stepId) => {
        // Only allow navigation to steps you've actually reached, or current step
        const canNavigate = stepId <= maxReachedStep;
        
        if (!canNavigate) return;

        // Save current step data before navigating
        saveCurrentStepData();

        // Navigate to the selected step
        loadStepData(stepId);
    };

    // Handle subsection navigation within campaign details step
    const handleSubsectionClick = (subsectionName) => {
        console.log('handleSubsectionClick called with:', subsectionName);
        console.log('selectedOption:', selectedOption, 'currentStep:', currentStep);
        console.log('campaignData.platform:', campaignData.platform);
        console.log('currentView before:', currentView);
        console.log('maxReachedSubsection:', maxReachedSubsection);
        
        // Allow subsection navigation if we've reached step 3 at some point
        const hasReachedCampaignDetails = (selectedOption === 'blank' && maxReachedStep >= 2) || 
                                         (selectedOption !== 'blank' && maxReachedStep >= 3);
        
        console.log('hasReachedCampaignDetails:', hasReachedCampaignDetails);
        
        if (!hasReachedCampaignDetails) return;

        // Define subsection order for progress tracking
        const subsectionOrder = ['name', 'salesforce-number', 'details-recap'];
        const targetSubsection = subsectionName.toLowerCase().replace(' ', '-');
        
        // Check if user can access this subsection
        const currentIndex = subsectionOrder.indexOf(maxReachedSubsection);
        const targetIndex = subsectionOrder.indexOf(targetSubsection);
        
        console.log('currentIndex:', currentIndex, 'targetIndex:', targetIndex);
        console.log('targetSubsection:', targetSubsection);
        
        // Only allow navigation to reached subsections (not the next one)
        if (targetIndex > currentIndex) {
            console.log('Cannot access subsection - not reached yet');
            return;
        }

        // Navigate to appropriate view based on subsection
        // First, ensure we're on the correct step
        const correctStep = selectedOption === 'blank' ? 2 : 3;
        if (currentStep !== correctStep) {
            setCurrentStep(correctStep);
        }
        
        switch (subsectionName) {
            case 'Name':
                console.log('Setting view to campaign-details');
                setCurrentView('campaign-details');
                break;
            case 'Salesforce number':
                console.log('Salesforce number case - platform check:', campaignData.platform === 'Twitch');
                if (campaignData.platform === 'Twitch') {
                    console.log('Setting view to salesforce-number');
                    setCurrentView('salesforce-number');
                } else {
                    console.log('Platform is not Twitch, staying on current view');
                }
                break;
            case 'Details recap':
                console.log('Setting view to details-recap');
                setCurrentView('details-recap');
                break;
            default:
                console.log('Unknown subsection name:', subsectionName);
                break;
        }
    };

    const isCurrentStepCompleted = () => {
        switch (currentStep) {
            case 1:
                return selectedOption !== null;
            case 2:
                return selectedTemplates.length > 0 || selectedOption !== 'templates';
            case 3:
                return true; // Campaign details is the final step
            default:
                return false;
        }
    };

    // Continue button handler for step progression within campaign details
    const handleContinue = () => {
        // For blank canvas: step 2 is campaign details
        if (selectedOption === 'blank' && currentStep === 2 && campaignData.platform === 'Twitch') {
            // Within step 2 (campaign details for blank), switch to salesforce number view
            setCurrentView('salesforce-number');
            setMaxReachedSubsection('salesforce-number');
        } 
        // For templates/duplicate: step 3 is campaign details
        else if ((selectedOption === 'templates' || selectedOption === 'duplicate') && currentStep === 3 && campaignData.platform === 'Twitch') {
            // Within step 3 (campaign details for templates/duplicate), switch to salesforce number view
            setCurrentView('salesforce-number');
            setMaxReachedSubsection('salesforce-number');
        } 
        // For Amazon platform, go directly to details recap
        else if (campaignData.platform === 'Amazon') {
            setCurrentView('details-recap');
            setMaxReachedSubsection('details-recap');
        } else {
            // For other cases, this might go to final step or finish
            console.log('Campaign setup complete!', { campaignData, salesforceData });
        }
    };

    const saveCurrentStepData = () => {
        const stepData = currentStep === 1 
            ? { selectedOption }
            : currentStep === 2 
            ? { 
                selectedCategory, 
                templateType, 
                selectedTemplates: [...selectedTemplates], 
                selectedFeatureFilters: [...selectedFeatureFilters],
                searchTerm,
                currentView,
                selectedCampaignToDuplicate
            }
            : currentStep === 3
            ? {
                currentView,
                maxReachedSubsection,
                campaignData: {...campaignData},
                salesforceData: {...salesforceData}
            }
            : {};
            
        console.log(`Saving step ${currentStep} data:`, stepData);
        
        setSavedStepData(prev => ({
            ...prev,
            [`step${currentStep}`]: stepData
        }));
    };

    const loadStepData = (stepId) => {
        if (stepId === 1) {
            // Just navigate back to step 1, don't change any selections
            setCurrentStep(1);
            setCurrentView('starter');
            // Keep all current selections intact
        } else if (stepId === 2) {
            // For blank canvas, step 2 is campaign details
            if (selectedOption === 'blank') {
                setCurrentStep(2);
                setCurrentView('campaign-details');
                // Campaign data should already be preserved in campaignData state
                return;
            }
            
            // For templates/duplicate flows, step 2 is templates/duplicate selection
            // Navigate back to step 2, determine correct view based on selectedOption
            if (selectedOption === 'templates') {
                // For templates flow, restore saved template state if available
                const stepData = savedStepData[`step${stepId}`];
                if (stepData) {
                    setSelectedCategory(stepData.selectedCategory);
                    setTemplateType(stepData.templateType || 'feature');
                    setSelectedTemplates([...(stepData.selectedTemplates || [])]);
                    setSelectedFeatureFilters([...(stepData.selectedFeatureFilters || [])]);
                    setSearchTerm(stepData.searchTerm || '');
                    setCurrentView(stepData.currentView || 'categories');
                } else {
                    // Default to categories view for templates
                    setCurrentView('categories');
                }
            } else if (selectedOption === 'duplicate') {
                // For campaign duplicate flow, go to campaign-duplicates view
                setCurrentView('campaign-duplicates');
                // Restore saved campaign selection if available
                const stepData = savedStepData[`step${stepId}`];
                if (stepData && stepData.selectedCampaignToDuplicate) {
                    setSelectedCampaignToDuplicate(stepData.selectedCampaignToDuplicate);
                }
            } else {
                // Fallback to determine view based on saved data
                const stepData = savedStepData[`step${stepId}`];
                if (stepData && stepData.currentView) {
                    setCurrentView(stepData.currentView);
                    // Restore appropriate data based on view
                    if (stepData.currentView === 'campaign-duplicates') {
                        if (stepData.selectedCampaignToDuplicate) {
                            setSelectedCampaignToDuplicate(stepData.selectedCampaignToDuplicate);
                        }
                    } else {
                        setSelectedCategory(stepData.selectedCategory);
                        setTemplateType(stepData.templateType);
                        setSelectedTemplates([...(stepData.selectedTemplates || [])]);
                        setSelectedFeatureFilters([...(stepData.selectedFeatureFilters || [])]);
                        setSearchTerm(stepData.searchTerm || '');
                    }
                } else {
                    setCurrentView('categories');
                }
            }
            setCurrentStep(2);
        } else if (stepId === 3) {
            // Step 3 only exists for templates/duplicate flows (not blank canvas)
            if (selectedOption === 'blank') {
                return; // No step 3 for blank canvas
            }
            
            // Navigate back to step 3 (Campaign Details)
            setCurrentStep(3);
            
            // Restore saved step 3 data if available
            const stepData = savedStepData[`step${stepId}`];
            console.log('Loading step 3 data:', stepData);
            if (stepData) {
                setCurrentView(stepData.currentView || 'campaign-details');
                if (stepData.maxReachedSubsection) {
                    console.log('Restoring maxReachedSubsection:', stepData.maxReachedSubsection);
                    setMaxReachedSubsection(stepData.maxReachedSubsection);
                }
                // Restore campaign and salesforce data if needed
                if (stepData.campaignData) {
                    setCampaignData(stepData.campaignData);
                }
                if (stepData.salesforceData) {
                    setSalesforceData(stepData.salesforceData);
                }
            } else {
                console.log('No saved step 3 data found');
                setCurrentView('campaign-details');
            }
        }
    };

    const getStepStatus = (step) => {
        if (step.id < currentStep) return 'completed';
        if (step.id === currentStep) return 'current';
        return 'upcoming';
    };

    const getFilteredTemplates = () => {
        const resolvedCategoryId = selectedCategory || (getFilteredCategories().length === 1 ? getItemId(getFilteredCategories()[0]) : null);
        let filtered = allTemplates.filter(template => 
            templateHasCategory(template, resolvedCategoryId) &&
            template.templateType === templateType
        );

        if (selectedFeatureFilters.length > 0) {
            filtered = filtered.filter(template => 
                selectedFeatureFilters.includes(template.featureType)
            );
        }

        if (searchTerm) {
            filtered = filtered.filter(template => 
                template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                template.featureType.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    };

    const getFilteredCategories = () => {
        // Filter categories to only show those that have at least one template of the current templateType
        const categoriesWithTemplates = templateCategories.filter(category => {
            const categoryId = getItemId(category);
            const categoryTemplates = allTemplates.filter(template => 
                templateHasCategory(template, categoryId) &&
                template.templateType === templateType
            );
            
            return categoryTemplates.length > 0;
        });

        // If no feature filters are selected, show all categories with templates
        if (selectedFeatureFilters.length === 0) {
            return categoriesWithTemplates;
        }

        // Filter categories to only show those that have templates matching the selected feature filters
        return categoriesWithTemplates.filter(category => {
            const categoryId = getItemId(category);
            const categoryTemplates = allTemplates.filter(template => 
                templateHasCategory(template, categoryId) &&
                template.templateType === templateType
            );
            
            // Check if this category has any templates that match the selected feature filters
            return categoryTemplates.some(template => 
                selectedFeatureFilters.includes(template.featureType)
            );
        });
    };

    // Render campaign duplicate selection view
    const renderCampaignDuplicates = () => (
        <>
            <div className="step-header">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
                        <span className="step-label">Step 0{currentStep}</span>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: "7px"}}>
                            <h3>Campaign duplicate</h3>
                            <input
                                type="text"
                                placeholder="Search campaigns..."
                                value={campaignSearchTerm}
                                onChange={handleSearchChange}
                                className="duplicate-search-input"
                            />
                        </div>
                        <p className="step-description">
                            Select an existing campaign to duplicate. The new campaign will use the same structure and features.
                        </p>
                    </div>
                </div>
            </div>

            <div className="content-page-container" style={{ padding: "0", maxWidth: "none", width: "100%" }}>
                <div className="">
                    <div className="fl-column grow">
                        {/* Tabs */}
                        <div className="campaign-tabs" style={{ display: "flex", marginBottom: 16 }}>
                            <div
                                className={`campaign-tab${activeCampaignTab === TAB_MY ? " active" : ""}`}
                                onClick={() => setActiveCampaignTab(TAB_MY)}
                            >
                                My Campaigns
                            </div>
                            <div
                                className={`campaign-tab${activeCampaignTab === TAB_STARRED ? " active" : ""}`}
                                onClick={() => setActiveCampaignTab(TAB_STARRED)}
                            >
                                Starred
                            </div>
                            <div
                                className={`campaign-tab${activeCampaignTab === TAB_ARCHIVED ? " active" : ""}`}
                                onClick={() => setActiveCampaignTab(TAB_ARCHIVED)}
                            >
                                Archived
                            </div>
                            <div
                                className={`campaign-tab${activeCampaignTab === TAB_ALL ? " active" : ""}`}
                                onClick={() => setActiveCampaignTab(TAB_ALL)}
                            >
                                All Campaigns
                            </div>
                        </div>
                        
                        {/* Filters */}
                        <div className="filters-container form-holder" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", flex: 1 }}>
                                {/* People filter */}
                                <AdvancedDropDownList
                                    id="peopleFilter"
                                    items={allUsers}
                                    selectedItems={peopleFilter}
                                    onSelect={user => {
                                        if (peopleFilter.some(p => p._id === user._id)) {
                                            setPeopleFilter(peopleFilter.filter(p => p._id !== user._id));
                                        } else {
                                            setPeopleFilter([...peopleFilter, { _id: user._id, name: user.name, type: null }]);
                                        }
                                    }}
                                    summary={peopleSummary}
                                    placeholder="Add People"
                                    submenuOptionsFunc={user => ["owner", "shared", "either"]}
                                    onSubmenuSelect={(user, type) => {
                                        if (peopleFilter.some(p => p._id === user._id && p.type === type)) {
                                            setPeopleFilter(peopleFilter.filter(p => !(p._id === user._id && p.type === type)));
                                        } else {
                                            setPeopleFilter([...peopleFilter, { _id: user._id, name: user.name, type }]);
                                        }
                                    }}
                                    getSelectedSubmenu={user => peopleFilter.filter(p => p._id === user._id).map(p => p.type).filter(Boolean)}
                                    className="filter"
                                />
                                
                                {/* Sort dropdown */}
                                <DropDownList
                                    id="sortFilter"
                                    selectFunc={handleSortOption}
                                    currentIndex={getIndexByValue(CAMPAIGN_SORT_OPTIONS, sortOption)}
                                    deselectedLabel="Sort By"
                                    items={CAMPAIGN_SORT_OPTIONS.map((opt, idx) => ({
                                        id: opt.value,
                                        label: opt.label,
                                        index: idx,
                                        value: opt.value
                                    }))}
                                    className="filter"
                                    showErrors={false}
                                    enabled={true}
                                />
                                
                                {/* Status filter */}
                                <DropDownList
                                    id="statusFilter"
                                    selectFunc={handleStatusOption}
                                    currentIndex={getIndexByValue(CAMPAIGN_STATUS_OPTIONS, statusFilter)}
                                    deselectedLabel="Status"
                                    items={CAMPAIGN_STATUS_OPTIONS.map((opt, idx) => ({
                                        id: opt.value,
                                        label: opt.label,
                                        index: idx,
                                        value: opt.value,
                                        deselect: opt.deselect
                                    }))}
                                    className="filter"
                                    showErrors={false}
                                    enabled={true}
                                />
                                
                                {/* Client filter */}
                                <AdvancedDropDownList
                                    id="clientFilter"
                                    items={allClients}
                                    selectedItems={clientFilter}
                                    onSelect={client => {
                                        if (clientFilter.some(c => c.clientName === client.clientName)) {
                                            setClientFilter(clientFilter.filter(c => c.clientName !== client.clientName));
                                        } else {
                                            setClientFilter([...clientFilter, { clientName: client.clientName, region: null }]);
                                        }
                                    }}
                                    summary={clientSummary}
                                    placeholder="Add Client"
                                    submenuOptionsFunc={client => client.regions || []}
                                    onSubmenuSelect={(client, region) => {
                                        if (clientFilter.some(c => c.clientName === client.clientName && c.region === region)) {
                                            setClientFilter(clientFilter.filter(c => !(c.clientName === client.clientName && c.region === region)));
                                        } else {
                                            setClientFilter([...clientFilter, { clientName: client.clientName, region }]);
                                        }
                                    }}
                                    getSelectedSubmenu={client => clientFilter.filter(c => c.clientName === client.clientName).map(c => c.region).filter(Boolean)}
                                    className="filter"
                                />
                                
                                {/* Modified filter */}
                                <DropDownList
                                    id="modifiedFilter"
                                    selectFunc={handleModifiedOption}
                                    currentIndex={getIndexByValue(MODIFIED_OPTIONS, modifiedOption)}
                                    deselectedLabel="Modified"
                                    items={MODIFIED_OPTIONS.map((opt, idx) => ({
                                        id: opt.value,
                                        label: opt.label,
                                        index: idx,
                                        value: opt.value,
                                        deselect: opt.deselect
                                    }))}
                                    className="filter"
                                    showErrors={false}
                                    enabled={true}
                                />
                            </div>
                        </div>
                        
                        {/* List view */}
                        <div className="full-list-scroll">
                            <div className="clist-headings-container grow">
                                <div className='fl-row list-view-content'>
                                    <div className='fl-row list-view-data-block fixed-icon'>
                                    </div>
                                    <div className='fl-row list-view-data-block fixed-icon'>
                                    </div>    
                                    <div className='fl-row list-view-data-block wide'>
                                        Campaign Title
                                    </div>
                                    <div className='fl-row list-view-data-block wide'>
                                        Owner
                                    </div>
                                    <div className='fl-row list-view-data-block'>
                                        Status
                                    </div>
                                    <div className='fl-row list-view-data-block'>
                                        Client
                                    </div>
                                    <div className='fl-row list-view-data-block narrow'>
                                        Last Modified
                                    </div>
                                </div>
                            </div>
                            
                            <div className="scroll-container scroll-list">
                                {campaignsData.map((campaign) => (
                                    <div
                                        key={campaign._id}
                                        className={`campaign-row ${selectedCampaignToDuplicate === campaign._id ? 'selected' : ''}`}
                                        onClick={() => handleCampaignSelect(campaign._id)}
                                    >
                                        <div className='fl-row list-view-content'>
                                            <div className='fl-row list-view-data-block fixed-icon'>
                                                <div className={`radio-button${selectedCampaignToDuplicate === campaign._id ? ' radio-checked' : ''}`}></div>
                                            </div>
                                            <div className='fl-row list-view-data-block fixed-icon'>
                                                {((authContext?.userData?.starredCampaigns?.map(id => id) || []).indexOf(campaign._id) !== -1) && 
                                                    IconJsxer.GetIcon(IconJsxer.ICONS.star, IconJsxer.ICON_STYLES.listIcon)
                                                }
                                            </div>    
                                            <div className='fl-row list-view-data-block wide'>
                                                {campaign.campaignName}
                                            </div>
                                            <div className='fl-row list-view-data-block wide'>
                                                {campaign.campaignOwnerName}
                                            </div>
                                            <div className='fl-row list-view-data-block'>
                                                {campaign.status}
                                            </div>
                                            <div className='fl-row list-view-data-block'>
                                                {campaign.client}
                                            </div>
                                            <div className='fl-row list-view-data-block narrow'>
                                                {campaign.updatedAt 
                                                    ? new Date(campaign.updatedAt).toLocaleDateString() 
                                                    : campaign.lastModified 
                                                        ? new Date(campaign.lastModified).toLocaleDateString()
                                                        : 'N/A'
                                                }
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    // Render different views based on current state
    const renderStarterOptions = () => (
        <>
            <div className="step-header">
                <span className="step-label">Step 0{currentStep}</span>
                <h3>Starter option</h3>
                <p className="step-description">
                    Begin with a feature template or a campaign duplicate.
                </p>
            </div>

            <div className="starter-options">
                {starterOptions.map((option) => (
                    <div
                        key={option.id}
                        className={`starter-option ${selectedOption === option.id ? 'selected' : ''}`}
                        data-option={option.id}
                        onClick={() => handleOptionSelect(option.id)}
                    >
                        <div className="option-radio">
                            <div className={`radio-button${selectedOption === option.id ? ' radio-checked' : ''}`}></div>
                        </div>
                        <div className="option-icon">
                            <img src={option.icon} alt={option.title} />
                        </div>
                        <div className="option-content">
                            <h3>{option.title}</h3>
                            <p className="option-description">{option.description}</p>
                            <p className="option-subtext">{option.subText}</p>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );

    const renderCategories = () => (
        <>
            <div className="step-header">
                <div className="header-top-row">
                    <div className="header-left">
                        <span className="step-label">Step 0{currentStep}</span>
                        <h3>Templates</h3>
                    </div>
                    <div className="spacing-line"></div>
                    <div className="search-input-container">
                        <input
                            type="text"
                            placeholder="Search by feature or content keywords"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        ></input>
                        {IconJsxer.GetIcon(IconJsxer.ICONS.search, IconJsxer.ICON_STYLES.searchBox)}
                    </div>
                </div>
                <p className="step-description">
                    Select one or multiple feature templates – you're free to edit, remove or add more later in your workspace.
                </p>
            </div>

            {templatesLoading ? (
                <div className="loading-container">
                    <p>Loading template categories...</p>
                </div>
            ) : (
                <>
                    <div className="template-controls">
                <div className="template-tabs">
                    <button 
                        className={`tab-button ${templateType === 'feature' ? 'active' : ''}`}
                        onClick={() => handleTemplateTypeChange('feature')}
                    >
                        Feature templates
                    </button>
                    <button 
                        className={`tab-button ${templateType === 'campaign' ? 'active' : ''}`}
                        onClick={() => handleTemplateTypeChange('campaign')}
                    >
                        Campaign templates
                    </button>
                </div>
            </div>

            <div className="filter-tags">
                {availableFeatureTypes.map((filter) => (
                    <button
                        key={filter}
                        className={`filter-tag ${selectedFeatureFilters.includes(filter) ? 'active' : ''}`}
                        onClick={() => handleFeatureFilterToggle(filter)}
                    >
                        {filter}
                    </button>
                ))}
            </div>

            <div className="categories-grid">
                {getFilteredCategories().map((category) => (
                    <div
                        key={getItemId(category)}
                        className="category-card"
                        onClick={() => handleCategorySelect(getItemId(category))}
                    >
                        <div className="category-image">
                            <div className="placeholder-image">CATEGORY IMG</div>
                        </div>
                        <div className="category-info">
                            <p className="category-type">{category.description || 'Template Category'}</p>
                            <h3 className="category-name">{category.name}</h3>
                        </div>
                    </div>
                ))}
            </div>
                </>
            )}
        </>
    );

    const renderTemplates = () => {
        const filteredTemplates = getFilteredTemplates();
        const TEMPLATES_PER_PAGE = 16;
        const templatesToShow = showAllTemplates ? filteredTemplates : filteredTemplates.slice(0, TEMPLATES_PER_PAGE);
        const hasMoreTemplates = filteredTemplates.length > TEMPLATES_PER_PAGE;
        const currentCategory = templateCategories.find(cat => getItemId(cat) === selectedCategory);

        return (
            <>
                <div className="step-header">
                    <div className="header-top-row">
                        <div className="header-left">
                            <div className="back-and-label-container">
                                <button className="back-button" onClick={handleBackToCategories}>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.chevronLeft, IconJsxer.ICON_STYLES.standard)}
                                    {/* <span className="back-text">{}</span> */}
                                </button>
                                <span className="step-label">Step 0{currentStep}</span>
                            </div>
                            <h3>Templates</h3>
                        </div>
                        <div className="spacing-line"></div>
                        <div className="search-input-container">
                            <input
                                type="text"
                                placeholder="Search by feature or content keywords"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {IconJsxer.GetIcon(IconJsxer.ICONS.search, IconJsxer.ICON_STYLES.searchBox)}
                        </div>
                    </div>
                    <p className="step-description">
                        Select one or multiple feature templates – you're free to edit, remove or add more later in your workspace.
                    </p>
                </div>

                <div className="template-controls">
                    <div className="template-tabs">
                        <button 
                            className={`tab-button ${templateType === 'feature' ? 'active' : ''}`}
                            onClick={() => handleTemplateTypeChange('feature')}
                        >
                            Feature templates
                        </button>
                        <button 
                            className={`tab-button ${templateType === 'campaign' ? 'active' : ''}`}
                            onClick={() => handleTemplateTypeChange('campaign')}
                        >
                            Campaign templates
                        </button>
                    </div>
                </div>

                <div className="filter-tags">
                    {availableFeatureTypes.map((filter) => (
                        <button
                            key={filter}
                            className={`filter-tag ${selectedFeatureFilters.includes(filter) ? 'active' : ''}`}
                            onClick={() => handleFeatureFilterToggle(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                <div className="templates-grid">
                    {templatesToShow.map((template) => {
                        const templateId = getItemId(template);
                        const selectionNumber = getTemplateSelectionNumber(templateId);
                        return (
                            <TemplateCardItem
                                key={templateId}
                                template={template}
                                isSelected={selectedTemplates.includes(templateId)}
                                selectionNumber={selectionNumber}
                                onToggle={handleTemplateToggle}
                            />
                        );
                    })}
                </div>

                {!templatesLoading && filteredTemplates.length === 0 && (
                    <div className="loading-container">
                        <p>No templates available for this selection yet.</p>
                    </div>
                )}

                {hasMoreTemplates && !showAllTemplates && (
                    <div className="show-more-container">
                        <button 
                            className="show-more-button"
                            onClick={() => setShowAllTemplates(true)}
                        >
                            Show more ({filteredTemplates.length - TEMPLATES_PER_PAGE} more)
                        </button>
                    </div>
                )}
            </>
        );
    };

    // Render campaign details view
    const renderCampaignDetails = () => (
        <>
            <div className="step-header">
                <span className="step-label">Step 0{currentStep}</span>
                <h3>Campaign details</h3>
                <p className="step-description">
                    Enter your campaign name and stream platform.
                </p>
            </div>

            <div className="campaign-details-form">
                <div className="form-group">
                    <label htmlFor="campaignName">Campaign name</label>
                    <input
                        type="text"
                        id="campaignName"
                        placeholder="Enter name"
                        value={campaignData.campaignName}
                        onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            campaignName: e.target.value
                        }))}
                        className="campaign-name-input"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="platform">Stream platform</label>
                    <select
                        id="platform"
                        value={campaignData.platform}
                        onChange={(e) => {
                            setCampaignData(prev => ({
                                ...prev,
                                platform: e.target.value
                            }));
                            // Reset subsection progress when platform changes
                            setMaxReachedSubsection('name');
                        }}
                        className="platform-select"
                    >
                        <option value="Twitch">Twitch</option>
                        <option value="Amazon">Amazon</option>
                    </select>
                </div>
            </div>
        </>
    );

    // Render Salesforce number step (part of step 3 for Twitch)
    const renderSalesforceNumber = () => (
        <>
            <div className="step-header">
                <span className="step-label">Step 0{currentStep}</span>
                <h3>Salesforce number</h3>
                <p className="step-description">
                    Enter your Salesforce number to import campaign details from AirTable.
                </p>
            </div>

            <div className="salesforce-form">
                <div className="form-group">
                    <label htmlFor="sfNumber">Salesforce number</label>
                    <div className="salesforce-input-group">
                        <input
                            type="text"
                            id="sfNumber"
                            placeholder="Enter Salesforce number"
                            value={salesforceData.sfNumber}
                            onChange={(e) => {
                                setSalesforceData(prev => ({
                                    ...prev,
                                    sfNumber: e.target.value,
                                    isVerified: false,
                                    error: ''
                                }))
                                setCampaignData(prev => ({
                                    ...prev,
                                    draft: false
                                }))
                            }}
                            className="salesforce-input"
                        />
                        <button
                            type="button"
                            onClick={() => fetchAirTableDataWithNumber(salesforceData.sfNumber)}
                            disabled={!salesforceData.sfNumber.trim() || salesforceData.isLoading}
                            className={`submit-btn ${(!salesforceData.sfNumber.trim() || salesforceData.isLoading) ? 'disabled' : ''}`}
                        >
                            {salesforceData.isLoading ? 'Verifying...' : 'Submit'}
                        </button>
                    </div>
                    
                    {salesforceData.error && (
                        <div className="error-message">
                            <span className="error-icon">⚠</span>
                            {salesforceData.error}
                        </div>
                    )}
                    
                    {salesforceData.isVerified && (
                        <div className="success-message">
                            <span className="success-icon">✓</span>
                            Campaign details imported successfully from AirTable!
                        </div>
                    )}
                </div>

                <div className="salesforce-actions">
                    <button
                        type="button"
                        onClick={() => {
                            // Skip Salesforce number and go to details recap
                            setSalesforceData({
                                sfNumber: '',
                                isVerified: false,
                                isLoading: false,
                                error: '',
                                airtableData: null
                            });
                            setCurrentView('details-recap');
                            setMaxReachedSubsection('details-recap');
                        }}
                        className="skip-link"
                    >
                        Continue without a Salesforce number
                    </button>
                </div>
            </div>
        </>
    );

    const renderDetailsRecap = () => {
        const isAmazonPlatform = campaignData.platform === 'Amazon';
        const isTwitchWithSalesforce = campaignData.platform === 'Twitch' && salesforceData.isVerified;
        const isTwitchWithoutSalesforce = campaignData.platform === 'Twitch' && !salesforceData.isVerified;

        return (
            <>
                <div className="step-header">
                    <span className="step-label">Step 0{currentStep}</span>
                    <h3>Campaign details</h3>
                    <p className="step-description">
                        Check your campaign details
                    </p>
                </div>

                <div className="details-recap">
                    <div className="recap-section">
                        <div className="recap-item">
                            <span className="recap-label">Campaign name</span>
                            <span className="recap-value">{campaignData.campaignName}</span>
                        </div>

                        {(isTwitchWithSalesforce || isTwitchWithoutSalesforce || isAmazonPlatform) && (
                            <div className="recap-item">
                                <span className="recap-label">Salesforce number</span>
                                <span className="recap-value">
                                    {isTwitchWithSalesforce ? (
                                        salesforceData.sfNumber
                                    ) : isTwitchWithoutSalesforce ? (
                                        <span className="not-provided">
                                            Not provided
                                            <button 
                                                className="enter-number-link"
                                                onClick={() => setCurrentView('salesforce-number')}
                                            >
                                                Enter Salesforce number
                                            </button>
                                        </span>
                                    ) : (
                                        'Not provided'
                                    )}
                                </span>
                            </div>
                        )}

                        <div className="recap-item">
                            <span className="recap-label">Status</span>
                            <span className="recap-value recap-status">
                                {isTwitchWithSalesforce || isAmazonPlatform ? 'Active' : 'Inactive'}
                                <div className="info-tooltip">
                                    <span className="status-info-icon">i</span>
                                    <span className="tooltip-text">
                                        {isTwitchWithSalesforce && 
                                            'Your campaign status is active when you enter a valid number'
                                        }
                                        {isAmazonPlatform && 
                                            'Your campaign status is active by default when streaming on Amazon Live'
                                        }
                                        {isTwitchWithoutSalesforce && 
                                            'Your campaign status is inactive and displays a watermark when you enter a valid number'
                                        }
                                    </span>
                                </div>
                            </span>
                        </div>

                        {(isTwitchWithSalesforce || isAmazonPlatform) && (
                            <div className="recap-item">
                                <span className="recap-label">Client</span>
                                {isAmazonPlatform ? (
                                    <div className="combo-input-container">
                                        <input
                                            type="text"
                                            placeholder="Enter or select client"
                                            value={Array.isArray(campaignData.client) ? campaignData.client.join(', ') : campaignData.client}
                                            onChange={(e) => setCampaignData(prev => ({
                                                ...prev,
                                                client: e.target.value
                                            }))}
                                            className="combo-input"
                                        />
                                        <select
                                            value=""
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    setCampaignData(prev => ({
                                                        ...prev,
                                                        client: e.target.value
                                                    }));
                                                }
                                            }}
                                            className="combo-dropdown"
                                        >
                                            <option value="">Select client</option>
                                            {allClients.map(client => (
                                                <option key={client.clientName} value={client.clientName}>
                                                    {client.clientName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <span className="recap-value">
                                        {(Array.isArray(salesforceData.airtableData?.Brand) ? salesforceData.airtableData.Brand.join(', ') : salesforceData.airtableData?.Brand) || ''}
                                    </span>
                                )}
                            </div>
                        )}

                        {(isTwitchWithSalesforce || isAmazonPlatform) && (
                            <div className="recap-item">
                                <span className="recap-label">Region</span>
                                {isAmazonPlatform ? (
                                    <div className="combo-input-container">
                                        <input
                                            type="text"
                                            placeholder="Enter or select region"
                                            value={campaignData.region}
                                            onChange={(e) => setCampaignData(prev => ({
                                                ...prev,
                                                region: e.target.value
                                            }))}
                                            className="combo-input"
                                        />
                                        <select
                                            value=""
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    setCampaignData(prev => ({
                                                        ...prev,
                                                        region: e.target.value
                                                    }));
                                                }
                                            }}
                                            className="combo-dropdown"
                                        >
                                            <option value="">Select region</option>
                                            {uniqueRegions.map(region => (
                                                <option key={region} value={region}>
                                                    {region}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <span className="recap-value">
                                        {salesforceData.airtableData?.['Influencer Broadcast Country']?.[0] || 'North America'}
                                    </span>
                                )}
                            </div>
                        )}

                        {isTwitchWithSalesforce && (
                            <div className="recap-item">
                                <span className="recap-label">IO number</span>
                                <span className="recap-value">
                                    {salesforceData.airtableData?.['IO Number'] || '9635078745'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="campaign-setup-container">
            {/* Side Progress Tracker */}
            <div className="campaign-setup-sidebar">
                <h2>Campaign setup</h2>
                <div className="progress-steps">
                    {steps.map((step) => {
                        const stepStatus = getStepStatus(step);
                        const canNavigate = step.id <= maxReachedStep;
                        let isNameField;
                        let isSalesforceField;
                        let isDetailsRecapField;
                        
                        return (
                            <div 
                                key={step.id} 
                                className={`progress-step ${stepStatus} ${canNavigate ? 'clickable' : ''}`} 
                                data-step={step.id}
                                onClick={() => canNavigate && handleStepClick(step.id)}
                            >
                                <div className="step-indicator">
                                    <span className="step-number">{step.id}</span>
                                </div>
                                <div className="step-content">
                                    <div className="step-name">{getStepDisplayName(step)}</div>
                                    {getStepDetails(step).length > 0 && (
                                        <div className="step-details">
                                            {getStepDetails(step).map((detail, index) => {
                                                const isSelectTemplates = detail.text.includes('Select templates');
                                                const isSelectCampaign = detail.text.includes('Select campaign');
                                                
                                                // Also connect for selected campaign names in duplicate flow
                                                const isSelectedCampaignName = selectedOption === 'duplicate' && 
                                                    selectedCampaignToDuplicate && 
                                                    detail.type === 'selection' && 
                                                    !isSelectCampaign && 
                                                    !isSelectTemplates;
                                                
                                                // Step detection for campaign details (dynamic based on selectedOption)
                                                const isCampaignDetailsStep = (selectedOption === 'blank' && step.id === 2) || 
                                                                             (selectedOption !== 'blank' && step.id === 3);
                                                isNameField = (detail.type === 'selection' && detail.text === 'Name') || (detail.type === "value" && isNameField);
                                                isSalesforceField = (detail.type === 'selection' && detail.text === 'Salesforce number') || (detail.type === "value" && isSalesforceField);
                                                isDetailsRecapField = (detail.type === 'selection' && detail.text === 'Details recap') || (detail.type === "value" && isDetailsRecapField);
                                                
                                                // Determine if subsection is clickable (campaign details subsections)
                                                // Should be clickable if we've reached the campaign details step at some point
                                                const hasReachedCampaignDetails = (selectedOption === 'blank' && maxReachedStep >= 2) || 
                                                                                 (selectedOption !== 'blank' && maxReachedStep >= 3);
                                                
                                                let canClickThisSubsection = false;
                                                
                                                if (hasReachedCampaignDetails && (isNameField || isSalesforceField || isDetailsRecapField)) {
                                                    // This is a step-selection element for campaign details
                                                    const subsectionOrder = ['name', 'salesforce-number', 'details-recap'];
                                                    const targetSubsection = detail.text.toLowerCase().replace(' ', '-');
                                                    const currentIndex = subsectionOrder.indexOf(maxReachedSubsection);
                                                    const targetIndex = subsectionOrder.indexOf(targetSubsection);
                                                    canClickThisSubsection = targetIndex <= currentIndex;
                                                }
                                                // Note: step-value elements are NOT made clickable anymore
                                                
                                                // Debug logging for all elements
                                                console.log(`DEBUG ALL: ${detail.text} - type: ${detail.type}, canClick: ${canClickThisSubsection}`);
                                                
                                                // Debug logging
                                                if (detail.text === 'Name' || detail.text === 'Salesforce number' || detail.text === 'Details recap') {
                                                    console.log(`DEBUG: ${detail.text} - hasReachedCampaignDetails: ${hasReachedCampaignDetails}, maxReachedSubsection: ${maxReachedSubsection}, canClick: ${canClickThisSubsection}`);
                                                }
                                                
                                                // For campaign details step, handle connecting lines differently
                                                let shouldConnect = false;
                                                let shouldConnectDotted = false;
                                                if (isCampaignDetailsStep) {
                                                    if (isNameField) {
                                                        // Name always connects since it's the first step (always reached)
                                                        shouldConnect = true;
                                                    } else if (isSalesforceField) {
                                                        // Salesforce connects when we're in salesforce-number view or beyond
                                                        shouldConnect = currentView === 'salesforce-number' || currentView === 'details-recap';
                                                        // Show dotted line if we're in campaign-details (next available)
                                                        shouldConnectDotted = currentView === 'campaign-details';
                                                    } else if (isDetailsRecapField) {
                                                        // Details recap connects when we're in details-recap view
                                                        shouldConnect = currentView === 'details-recap';
                                                        // Show dotted line if we're in salesforce-number (next available)
                                                        shouldConnectDotted = currentView === 'salesforce-number';
                                                    }
                                                } else {
                                                    // For other steps, use original logic
                                                    shouldConnect = isSelectTemplates || isSelectCampaign || isSelectedCampaignName;
                                                }
                                                
                                                // Determine className based on detail type and step
                                                let className;
                                                if (detail.type === 'label') {
                                                    className = `step-label ${shouldConnect ? 'connect-to-step' : ''}`;
                                                } else if (detail.type === 'value') {
                                                    className = `step-value`;
                                                    if (isNameField) {
                                                        if (currentView === 'campaign-details') {
                                                            className += ' step3-active';
                                                        } else {
                                                            className += ' step3-inactive';
                                                        }
                                                        // Extend line when salesforce is active  
                                                        if (currentView === 'salesforce-number') {
                                                            className += ' step3-extend-line';
                                                        }
                                                    } else if (isSalesforceField) {
                                                        if (currentView === 'salesforce-number') {
                                                            className += ' step3-active';
                                                        } else {
                                                            className += ' step3-inactive';
                                                        }
                                                    } else if (isDetailsRecapField) {
                                                        if (currentView === 'details-recap') {
                                                            className += ' step3-active';
                                                        } else {
                                                            className += ' step3-inactive';
                                                        }
                                                    }
                                                } else if (detail.type === 'selection') {
                                                    // Add campaign details step specific classes
                                                    let stepClasses = '';
                                                    if (isCampaignDetailsStep) {
                                                        if (isNameField) {
                                                            stepClasses = 'step3-name';
                                                            if (currentView === 'campaign-details') {
                                                                stepClasses += ' step3-active';
                                                            } else {
                                                                stepClasses += ' step3-inactive';
                                                            }
                                                            // Extend line when salesforce is active  
                                                            if (currentView === 'salesforce-number') {
                                                                stepClasses += ' step3-extend-line';
                                                            }
                                                        } else if (isSalesforceField) {
                                                            stepClasses = 'step3-salesforce';
                                                            if (currentView === 'salesforce-number') {
                                                                stepClasses += ' step3-active';
                                                            } else {
                                                                stepClasses += ' step3-inactive';
                                                            }
                                                        } else if (isDetailsRecapField) {
                                                            stepClasses = 'step3-details-recap';
                                                            if (currentView === 'details-recap') {
                                                                stepClasses += ' step3-active';
                                                            } else {
                                                                stepClasses += ' step3-inactive';
                                                            }
                                                        }
                                                    }
                                                    className = `step-selection ${stepClasses} ${shouldConnect ? 'connect-to-step' : shouldConnectDotted ? 'connect-dotted' : ''} ${canClickThisSubsection ? 'clickable-subsection' : ''}`;;
                                                } else {
                                                    className = 'step-prompt';
                                                }
                                                
                                                return (
                                                    <div 
                                                        key={index} 
                                                        className={className}
                                                        data-select-templates={isSelectTemplates}
                                                        data-select-campaign={isSelectCampaign}
                                                        style={{ position: 'relative' }}
                                                        onClick={canClickThisSubsection ? (e) => {
                                                            console.log('Click handler triggered for:', detail.text);
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleSubsectionClick(detail.text);
                                                        } : (e) => {
                                                            // Prevent click from bubbling to parent clickable elements
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        {detail.text}
                                                        {/* Extended connecting line for campaign details step when needed */}
                                                        {isCampaignDetailsStep && isNameField && currentView === 'salesforce-number' && (
                                                            <div className="step3-extended-line"></div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Main Content */}
            <div className="campaign-setup-main">
                <div className="campaign-setup-content">
                    {currentView === 'starter' && renderStarterOptions()}
                    {currentView === 'categories' && renderCategories()}
                    {currentView === 'templates' && renderTemplates()}
                    {currentView === 'campaign-duplicates' && renderCampaignDuplicates()}
                    {currentView === 'campaign-details' && renderCampaignDetails()}
                    {currentView === 'salesforce-number' && renderSalesforceNumber()}
                    {currentView === 'details-recap' && renderDetailsRecap()}
                </div>

                {/* Bottom Action Button */}
                <div className="campaign-setup-footer">
                    {currentView === 'starter' && (
                        <button
                            className={`confirm-button ${selectedOption ? 'active' : 'inactive'}`}
                            onClick={handleConfirmOption}
                            disabled={!selectedOption}
                        >
                            Confirm starter option
                        </button>
                    )}
                    {(currentView === 'categories' || currentView === 'templates') && (
                        <button
                            className={`confirm-button ${selectedTemplates.length > 0 ? 'active' : 'inactive'}`}
                            onClick={handleConfirmTemplates}
                            disabled={selectedTemplates.length === 0}
                        >
                            {selectedTemplates.length > 1 ? 'Confirm templates' : 'Confirm template'}
                        </button>
                    )}
                    {currentView === 'campaign-duplicates' && (
                        <button
                            className={`confirm-button ${selectedCampaignToDuplicate ? 'active' : 'inactive'}`}
                            onClick={handleConfirmCampaignDuplicate}
                            disabled={!selectedCampaignToDuplicate}
                        >
                            Confirm campaign duplicate
                        </button>
                    )}
                    {currentView === 'campaign-details' && (
                        <button
                            className={`confirm-button ${campaignData.campaignName.trim() ? 'active' : 'inactive'}`}
                            onClick={() => {
                                if (campaignData.platform === 'Twitch') {
                                    handleContinue(); // Go to Salesforce number within step 3
                                } else {
                                    handleContinue(); // Go to details recap for Amazon
                                }
                            }}
                            disabled={!campaignData.campaignName.trim()}
                        >
                            {campaignData.platform === 'Twitch' ? 'Continue' : 'Continue'}
                        </button>
                    )}
                    {currentView === 'salesforce-number' && (
                        <button
                            className={`confirm-button ${salesforceData.isVerified ? 'active' : 'inactive'}`}
                            onClick={() => {
                                setCurrentView('details-recap');
                                setMaxReachedSubsection('details-recap');
                            }}
                            disabled={!salesforceData.isVerified}
                        >
                            Continue
                        </button>
                    )}
                    {currentView === 'details-recap' && (
                        <button
                            className="confirm-button active"
                            onClick={() => handleCreateCampaign()}
                        >
                            Create campaign
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CampaignSetup;